import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

var DTS_SELECTION_STORAGE_KEY = 'dts_selection_v1';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  #shopifyCartCount = 0;

  /** @type {number} */
  #dtsCount = 0;

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  get #displayCount() {
    return this.#shopifyCartCount + this.#dtsCount;
  }

  connectedCallback() {
    super.connectedCallback();

    const shopifyAttr =
      this.refs.cartBubbleCount?.getAttribute('data-shopify-cart-count') ||
      this.refs.cartBubble?.getAttribute('data-shopify-cart-count');
    const parsedShopify = parseInt(shopifyAttr ?? '', 10);
    this.#shopifyCartCount = Number.isFinite(parsedShopify) ? parsedShopify : this.currentCartCount;
    this.#dtsCount = this.#readDtsCount();
    this.renderCartBubble(this.#displayCount, false);

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    document.addEventListener('try-on:selection-changed', this.onDtsSelectionChanged);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    document.removeEventListener('try-on:selection-changed', this.onDtsSelectionChanged);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * @returns {number}
   */
  #readDtsCount() {
    if (window.TryOnDTS) return window.TryOnDTS.count();

    try {
      var raw = sessionStorage.getItem(DTS_SELECTION_STORAGE_KEY);
      var parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.length : 0;
    } catch (_) {
      return 0;
    }
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.#dtsCount = this.#readDtsCount();
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';
    const resourceCount = event.detail.resource?.item_count;

    if (resourceCount !== undefined && resourceCount !== null) {
      this.#shopifyCartCount = resourceCount;
    } else if (comingFromProductForm) {
      this.#shopifyCartCount += itemCount;
    } else if (typeof event.detail.data?.itemCount === 'number') {
      this.#shopifyCartCount = itemCount;
    }

    /* Keep DTS count — cart morph must not wipe the combined badge. */
    this.#dtsCount = this.#readDtsCount();
    this.#syncShopifyCountAttrs();
    await this.renderCartBubble(this.#displayCount, true);

    /*
     * Other listeners on this same event (e.g. cart-items-component) can morph
     * the header section synchronously right after this handler runs, replacing
     * our combined textContent with the server-rendered, DTS-unaware count.
     * Re-assert once the current dispatch (and any same-tick morph) has settled.
     */
    queueMicrotask(this.ensureCartBubbleIsCorrect);
  };

  #syncShopifyCountAttrs() {
    const value = String(this.#shopifyCartCount);
    this.refs.cartBubble?.setAttribute('data-shopify-cart-count', value);
    this.refs.cartBubbleCount?.setAttribute('data-shopify-cart-count', value);
  }

  /**
   * @param {CustomEvent<{ products?: unknown[] }>} event
   */
  onDtsSelectionChanged = async (event) => {
    var prev = this.#dtsCount;
    var products = event.detail && event.detail.products;
    this.#dtsCount = Array.isArray(products) ? products.length : this.#readDtsCount();
    await this.renderCartBubble(this.#displayCount, this.#dtsCount > prev);
  };

  /**
   * Renders the cart bubble (Shopify cart + DTS selection).
   * @param {number} displayCount - Total count shown on the bubble.
   * @param {boolean} animate - Whether to animate the bubble.
   */
  renderCartBubble = async (displayCount, animate = true) => {
    this.refs.cartBubbleCount.classList.toggle('hidden', displayCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', displayCount === 0);

    this.currentCartCount = displayCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', displayCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(displayCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || displayCount === 0) return;

    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    if (!this.refs.cartBubbleCount) return;

    const shopifyAttr =
      this.refs.cartBubbleCount.getAttribute('data-shopify-cart-count') ||
      this.refs.cartBubble?.getAttribute('data-shopify-cart-count');
    const parsedShopify = parseInt(shopifyAttr ?? '', 10);
    if (Number.isFinite(parsedShopify)) {
      this.#shopifyCartCount = parsedShopify;
    }

    this.#dtsCount = this.#readDtsCount();
    this.renderCartBubble(this.#displayCount, false);
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
